import theme from '@frete.com/fuel-foundation/fretebras/theme.json';
import { FuelTag, FuelTypography } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { DS_TOKENS } from 'constants/styled.contants';
import { useGetTruckerQueriesData } from 'hooks/useGetTruckerQueriesData';
import React, { useEffect, useMemo, useState } from 'react';
import { isApprovalValidGR } from 'utils/isApprovalValid';
import { isEmpty } from 'utils/validate';
import { useGetRiskManagementLastConsultations } from 'hooks/queries/useGetRiskManagementLastConsultations';
import { useAppContext } from 'hooks/useAppContext';
import Card from './Card';
import { PartnerOptions } from './SelectPartner/types';
import SelectPartner from './SelectPartner';

const RiskManagement: React.FC = () => {
  const { truckerData } = useGetTruckerQueriesData();
  const [uniquePartnerNames, setUniquePartnerNames] = useState<string[]>();
  const {
    state: {
      profileSize: { isProfileSizeSmall },
    },
  } = useAppContext();

  const hasSomeApproval = truckerData?.lastSearches?.totalApprovedSearches > 0;
  const isApprovalValid = isApprovalValidGR({
    hasSomeApproval,
    lastApprovedDate: truckerData?.lastSearches?.lastApprovedDate,
  });

  const hasLastSearches = !!truckerData?.lastSearches?.totalApprovedSearches;

  const {
    isLoading: isLoadingLastConsultations,
    isFetching: isFetchingLastConsultations,
    isError: isErrorLastConsultations,
    isLoadingError: isLoadingErrorLastConsultations,
    isRefetchError: isRefetchErrorLastConsultations,
    isFetched: isFetchedLastConsultations,
    data: lastConsultationsData,
  } = useGetRiskManagementLastConsultations(truckerData?.trucker?.id);

  useEffect(() => {
    setUniquePartnerNames(Array.from(new Set(lastConsultationsData?.data?.map((item) => item.partner.name))));
  }, [lastConsultationsData]);

  const isLoading = useMemo(
    () => isLoadingLastConsultations || isFetchingLastConsultations,
    [isLoadingLastConsultations, isFetchingLastConsultations]
  );

  const isError = useMemo(
    () =>
      (isErrorLastConsultations || isLoadingErrorLastConsultations || isRefetchErrorLastConsultations) && !isLoading,
    [isErrorLastConsultations, isLoadingErrorLastConsultations, isRefetchErrorLastConsultations, isLoading]
  );

  const isFetched = useMemo(
    () => (isFetchedLastConsultations || !isEmpty(lastConsultationsData?.data)) && !isError && !isLoading,
    [isFetchedLastConsultations, isError, isLoading, lastConsultationsData]
  );

  const showSelectPartnerApproved = isApprovalValid && isFetched && uniquePartnerNames && uniquePartnerNames.length > 0;

  return (
    <FuelFlex
      defaultProps={{
        justifyContent: 'space-between',
        flexDirection: 'column',
        customPadding: `${DS_TOKENS.spacing.km4} ${DS_TOKENS.spacing.km8} 0`,
      }}
    >
      <FuelFlex defaultProps={{ justifyContent: 'space-between', flexDirection: 'row' }}>
        <FuelTypography size="heading-sm" weight="bold" spacingBottom={parseFloat(theme.spacing.km6)}>
          Gerenciamento de Risco
        </FuelTypography>
        {isApprovalValid && !isProfileSizeSmall && (
          <FuelTag
            highlight
            color="blue"
            label="Já Aprovado em GR"
            shape="rectangle"
            icon="risk-management"
            iconStyle="filled"
            uppercase
            data-testid="approved-tag"
          />
        )}
      </FuelFlex>
      <Card
        isApprovalValid={isApprovalValid}
        totalApproves={truckerData?.lastSearches?.totalApprovedSearches}
        hasLastSearches={hasLastSearches}
        isFetched={isFetched}
        lastConsultationsData={lastConsultationsData}
        uniquePartnerNames={uniquePartnerNames}
      />
      {!isApprovalValid && (
        <SelectPartner partners={['skymark', 'guep'] as PartnerOptions[]} flowType="notValidApproval" />
      )}
      {showSelectPartnerApproved && (
        <SelectPartner partners={uniquePartnerNames as PartnerOptions[]} flowType="validApproval" />
      )}
    </FuelFlex>
  );
};

export default RiskManagement;
